import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Package, Calendar, Layers, FileText, Clock, Banknote } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useProductionPersonDetail } from "../hooks/useProductionPersonDetail";
import { t } from "@/shared/lib/th-translations";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import ZScoreLegend from "../components/production/ZScoreLegend";
import { zScoreColor, hasEnoughBaseline } from "@/frontend/lib/production-stats";

type WindowDays = 1 | 14 | 30 | 60 | 90;
type DetailMetric = "good_units" | "shots" | "value";

const detailMetricOptions: { key: string; value: DetailMetric }[] = [
  { key: "production.people.detail.metric.goodUnits", value: "good_units" },
  { key: "production.people.detail.metric.shots", value: "shots" },
  { key: "production.people.detail.metric.value", value: "value" },
];

const windowOptions: { key: string; value: WindowDays }[] = [
  { key: "production.people.detail.window.today", value: 1 },
  { key: "production.people.detail.window.last14Days", value: 14 },
  { key: "production.people.detail.window.last30Days", value: 30 },
  { key: "production.people.detail.window.last60Days", value: 60 },
  { key: "production.people.detail.window.last90Days", value: 90 },
];

const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

function formatTHB(amount: number): string {
  return THB_FORMATTER.format(amount);
}

const PIE_COLORS = [
  "#14b8a6",
  "#6366f1",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#10b981",
  "#f97316",
  "#06b6d4",
];

function formatDurationMs(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0) {
    return t("production.people.detail.format.hourMinute")
      .replace("{h}", String(hours))
      .replace("{m}", String(mins));
  }
  return t("production.people.detail.format.minute").replace("{m}", String(mins));
}

export default function ProductionPersonDetail() {
  const { employeeId: rawEmployeeId } = useParams<{ employeeId: string }>();
  const employeeId = rawEmployeeId ? decodeURIComponent(rawEmployeeId) : "";
  const [windowDays, setWindowDays] = useState<WindowDays>(30);
  const [metric, setMetric] = useState<DetailMetric>("shots");

  const { data, isLoading, error } = useProductionPersonDetail(
    employeeId || undefined,
    windowDays,
  );

  const responseData = data?.data;
  const employeeName = responseData?.employeeName ?? employeeId;
  const totals = responseData?.totals;
  const baseline = responseData?.baseline;
  const baselineShots = responseData?.baselineShots;
  const dailyThroughput = responseData?.dailyThroughput ?? [];
  const machineMix = responseData?.machineMix ?? [];
  const processMix = responseData?.processMix ?? [];
  const topJobs = responseData?.topJobs ?? [];
  const idleGaps = responseData?.idleGaps ?? [];

  const longestIdleGap =
    idleGaps.length > 0 ? Math.max(...idleGaps.map((g) => g.durationMs)) : null;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <Link
          to="/production/people"
          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          ← {t("production.people.detail.backToList")}
        </Link>
        <h1 className="text-3xl font-bold">
          {t("production.people.detail.title").replace("{name}", employeeName)}
        </h1>
        <p className="text-xs text-muted-foreground font-mono">{employeeId}</p>
      </div>

      {/* Caveat */}
      <Alert>
        <AlertDescription className="text-sm text-muted-foreground">
          {t("production.people.detail.caveat")}
        </AlertDescription>
      </Alert>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.people.detail.window.label")}:
          </span>
          <div className="flex gap-1">
            {windowOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={windowDays === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setWindowDays(opt.value)}
              >
                {t(opt.key)}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.people.detail.metric.label")}:
          </span>
          <div className="flex gap-1">
            {detailMetricOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={metric === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setMetric(opt.value)}
              >
                {t(opt.key)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{t("production.people.detail.error")}</AlertDescription>
        </Alert>
      )}

      {/* Stats row — 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {metric === "shots"
                ? t("production.people.detail.stats.totalShots")
                : metric === "value"
                  ? t("production.people.detail.stats.totalValue")
                  : t("production.people.detail.stats.totalGoodUnits")}
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : metric === "value" ? (
                formatTHB(totals?.value ?? 0)
              ) : (
                (metric === "shots"
                  ? (totals?.shots ?? 0)
                  : (totals?.goodUnits ?? 0)
                ).toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.detail.stats.totalValue")}
            </CardTitle>
            <Banknote className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                formatTHB(totals?.value ?? 0)
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.detail.stats.activeDays")}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (totals?.activeDays ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.detail.stats.uniqueMachines")}
            </CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (totals?.uniqueMachines ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.detail.stats.uniqueTasks")}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : (totals?.uniqueTasks ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.detail.stats.longestIdleGap")}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : longestIdleGap !== null ? (
                formatDurationMs(longestIdleGap)
              ) : (
                "—"
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Color hint (shared with machine-detail namespace — reuse) */}
      <ZScoreLegend namespace="production.people.detail.legend" />

      {/* Throughput trend — own daily */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.people.detail.sections.throughputTrend")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.people.detail.sections.throughputTrendSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : dailyThroughput.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.people.detail.empty.person")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={dailyThroughput}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  tickFormatter={(v: string) => v.slice(5)}
                  tick={{ fontSize: 12 }}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#14b8a6" />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  stroke="#8b5cf6"
                />
                <Tooltip
                  formatter={(value, name) => [
                    Number(value).toLocaleString(),
                    String(name),
                  ]}
                  labelFormatter={(label) => String(label)}
                />
                {(() => {
                  const bl = metric === "shots" ? baselineShots : baseline;
                  const yAxisId = metric === "shots" ? "right" : "left";
                  return bl && bl.count >= 3 ? (
                    <ReferenceLine
                      yAxisId={yAxisId}
                      y={bl.mean}
                      stroke="#f59e0b"
                      strokeDasharray="4 4"
                      label={{
                        value: `avg ${bl.mean.toFixed(0)}`,
                        position: "insideTopRight",
                        fontSize: 11,
                      }}
                    />
                  ) : null;
                })()}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="goodUnits"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name={t("production.people.detail.stats.totalGoodUnits")}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="shots"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name={t("production.people.detail.stats.totalShots")}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Machine mix with per-machine peer z-score */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t("production.people.detail.sections.machineRank")}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.people.detail.sections.machineRankSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : machineMix.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.people.detail.empty.machines")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.people.detail.machineTable.machine")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.people.detail.machineTable.primaryProcess")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {metric === "shots"
                        ? t("production.people.detail.machineTable.shots")
                        : metric === "value"
                          ? t("production.people.detail.machineTable.value")
                          : t("production.people.detail.machineTable.goodUnits")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.people.detail.machineTable.value")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.people.detail.machineTable.daysActive")}
                    </th>
                    <th className="px-3 py-2 text-center font-medium">
                      {t("production.people.detail.machineTable.rankOnMachine")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {machineMix.map((m) => {
                    const z = metric === "shots" ? m.zScoreShots : m.zScore;
                    const enough = hasEnoughBaseline(m.baselineN) && !m.soleOperator;
                    const rankClass = enough
                      ? zScoreColor(z)
                      : "bg-gray-100 text-gray-600";
                    const rankText = m.soleOperator
                      ? t("production.people.detail.machineTable.soleOperator")
                      : !hasEnoughBaseline(m.baselineN)
                        ? t("production.people.detail.machineTable.insufficientBaseline")
                        : z.toFixed(2);
                    const primaryValue =
                      metric === "shots"
                        ? m.shots
                        : metric === "value"
                          ? m.value
                          : m.goodUnits;
                    const primaryDisplay =
                      metric === "value"
                        ? formatTHB(primaryValue)
                        : primaryValue.toLocaleString();
                    return (
                      <tr key={m.machine} className="border-b hover:bg-gray-50/50">
                        <td className="px-3 py-2 font-medium">
                          <Link
                            to={`/production/machines/${encodeURIComponent(m.machine)}`}
                            className="text-blue-600 hover:underline"
                          >
                            {m.machine}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {m.primaryProcess || "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {primaryDisplay}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {formatTHB(m.value)}
                        </td>
                        <td className="px-3 py-2 text-right">{m.daysActive}</td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${rankClass}`}
                            title={
                              enough
                                ? `${t("production.people.detail.machineTable.zScoreTooltip")}: ${z.toFixed(2)}, n=${m.baselineN}`
                                : t(
                                    "production.people.detail.machineTable.noPeerComparison",
                                  )
                            }
                          >
                            {rankText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Process mix — pie + table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.people.detail.sections.processMix")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.people.detail.sections.processMixSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : processMix.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.people.detail.empty.processes")}
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-full md:w-72 flex-shrink-0">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                    <Pie
                      data={processMix}
                      dataKey={metric === "shots" ? "shots" : metric === "value" ? "value" : "goodUnits"}
                      nameKey="processTitle"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                    >
                      {processMix.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        metric === "value"
                          ? formatTHB(Number(value))
                          : Number(value).toLocaleString(),
                        metric === "shots"
                          ? t("production.people.detail.processTable.shots")
                          : metric === "value"
                            ? t("production.people.detail.processTable.value")
                            : t("production.people.detail.processTable.goodUnits"),
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="w-full overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-3 py-2 text-left font-medium">
                        {t("production.people.detail.processTable.process")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {metric === "shots"
                          ? t("production.people.detail.processTable.shots")
                          : metric === "value"
                            ? t("production.people.detail.processTable.value")
                            : t("production.people.detail.processTable.goodUnits")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("production.people.detail.processTable.value")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("production.people.detail.processTable.events")}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("production.people.detail.processTable.daysActive")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {processMix.map((p, i) => {
                      const primaryValue =
                        metric === "shots"
                          ? p.shots
                          : metric === "value"
                            ? p.value
                            : p.goodUnits;
                      const primaryDisplay =
                        metric === "value"
                          ? formatTHB(primaryValue)
                          : primaryValue.toLocaleString();
                      return (
                        <tr key={p.processTitle} className="border-b hover:bg-gray-50/50">
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-2">
                              <span
                                className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                                }}
                              />
                              {p.processTitle}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {primaryDisplay}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {formatTHB(p.value)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {p.events.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right">{p.daysActive}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top jobs */}
      <Card>
        <CardHeader>
          <CardTitle>{t("production.people.detail.sections.topJobs")}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {t("production.people.detail.sections.topJobsSubtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : topJobs.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              {t("production.people.detail.empty.jobs")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.people.detail.jobsTable.taskNumber")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("production.people.detail.jobsTable.customer")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.detail.jobsTable.paperWidth")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.detail.jobsTable.paperLength")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                      {t("production.people.detail.jobsTable.targetQuantity")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {metric === "shots"
                        ? t("production.people.detail.jobsTable.shots")
                        : metric === "value"
                          ? t("production.people.detail.jobsTable.value")
                          : t("production.people.detail.jobsTable.goodUnits")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.people.detail.jobsTable.value")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.people.detail.jobsTable.events")}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("production.people.detail.jobsTable.duration")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topJobs.map((job) => {
                    const primaryValue =
                      metric === "shots"
                        ? job.shots
                        : metric === "value"
                          ? job.value
                          : job.goodUnits;
                    const primaryDisplay =
                      metric === "value"
                        ? formatTHB(primaryValue)
                        : primaryValue.toLocaleString();
                    return (
                      <tr key={job.taskNumber} className="border-b hover:bg-gray-50/50">
                        <td className="px-3 py-2 font-mono text-xs">{job.taskNumber}</td>
                        <td
                          className="px-3 py-2 max-w-[160px] truncate"
                          title={job.customerName}
                        >
                          {job.customerName}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          {job.paperWidth > 0 ? job.paperWidth : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          {job.paperLength > 0 ? job.paperLength : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          {job.targetQuantity > 0
                            ? job.targetQuantity.toLocaleString()
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {primaryDisplay}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">
                          {formatTHB(job.value)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {job.events.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatDurationMs(job.lastSeenMs - job.firstSeenMs)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
